"use client";

import React from "react";
import { ShieldAlert, FileWarning, Briefcase, MailWarning, BrainCircuit, Laptop } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const challenges = [{
  title: "Inventory Checklist",
  description: "Create a detailed room-by-room list before we arrive. This ensures every box is accounted for and helps our team prioritize your most fragile items during the loading process.",
  icon: ShieldAlert
}, {
  title: "Labeling Strategy",
  description: "Mark boxes on the sides, not just the top. Use bold markers to indicate the destination room and whether the contents are fragile to speed up the unloading phase at your new home.",
  icon: FileWarning
}, {
  title: "Essential Box",
  description: "Pack a 'Day One' box with toiletries, chargers, basic tools, and snacks. Keep this with you in your personal vehicle so you aren't hunting for a toothbrush after a long day.",
  icon: Briefcase
}, {
  title: "Secure Electronics",
  description: "Take photos of the back of your TV and computer setups before disconnecting. This visual guide makes reassembling your home office or entertainment center a breeze.",
  icon: MailWarning
}, {
  title: "Clear Pathways",
  description: "Ensure hallways and entryways are free of clutter on moving day. This prevents accidents and allows our professional movers to work with maximum speed and precision.",
  icon: BrainCircuit
}, {
  title: "Utility Transfer",
  description: "Schedule your utility shut-offs for the day after you leave and activations for the day before you arrive. Moving into a dark house is a stress you don't need.",
  icon: Laptop
}];
export default function QuickTips() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="261090" id="quick-tips" className="py-24 bg-muted/30 relative overflow-hidden light">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px] opacity-50" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px] opacity-50" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-bold font-serif text-foreground tracking-tight uppercase">
            Pro Tips for a Seamless Shift
          </h2>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed font-default">
            Moving doesn't have to be chaotic. Follow these snackable bits of advice from our logistics experts to ensure your relocation is as efficient and stress-free as possible.
          </p>
        </div>

        <div ref={gridReveal.ref} className={`mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-200 ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {challenges.map((challenge, i) => {
          const Icon = challenge.icon;
          return <div data-index={i} key={i} className="h-full">
                <Card className="h-full border-border/40 shadow-md hover:shadow-lg transition-all duration-300 bg-card/80 backdrop-blur-sm rounded-lg">
                  <CardHeader>
                    <div className="size-12 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="size-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-default font-bold text-foreground">
                      {challenge.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-muted-foreground leading-relaxed font-default">
                      {challenge.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}